import { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Trash2 } from 'lucide-react';
import { parseISO, isValid, subDays } from 'date-fns';
import type { RepeatUnit } from '@/types';
import { computeNextDueDate, toISODate, formatDate } from '@/lib/dateUtils';

interface ReminderFormRowProps {
  index: number;
  onRemove: () => void;
}

const repeatUnits: { value: RepeatUnit; label: string }[] = [
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
  { value: 'years', label: 'years' }
];

export function ReminderFormRow({ index, onRemove }: ReminderFormRowProps) {
  const { register, control, watch, setValue } = useFormContext();
  const fieldPrefix = `reminders.${index}`;

  const oneTime = watch(`${fieldPrefix}.one_time`);
  const startDate = watch(`${fieldPrefix}.start_date`);
  const repeatEvery = watch(`${fieldPrefix}.repeat_every`);
  const repeatUnit: RepeatUnit = watch(`${fieldPrefix}.repeat_unit`);
  const remindBefore = watch(`${fieldPrefix}.remind_before_days`);

  useEffect(() => {
    if (oneTime) {
      setValue(`${fieldPrefix}.repeat_every`, 1);
      setValue(`${fieldPrefix}.repeat_unit`, 'years');
    }
  }, [oneTime, fieldPrefix, setValue]);

  // Compute next due date preview
  let nextDueDateStr = '';
  let alertDateStr = '';
  if (startDate) {
    const base = parseISO(startDate);
    if (isValid(base)) {
      const nextDue = oneTime ? base : computeNextDueDate(base, Number(repeatEvery) || 1, repeatUnit);
      nextDueDateStr = formatDate(toISODate(nextDue));
      const advanceDays = Number(remindBefore) || 0;
      if (advanceDays > 0) {
        alertDateStr = formatDate(toISODate(subDays(nextDue, advanceDays)));
      }
    }
  }

  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white';
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1';

  return (
    <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 space-y-3">
      {/* Title + remove */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className={labelCls}>Title</label>
          <input
            {...register(`${fieldPrefix}.title`, { required: true })}
            placeholder="e.g. Insurance Expiry"
            className={inputCls}
          />
        </div>
        <button type="button" onClick={onRemove} className="mt-5 p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Last service date */}
      <div>
        <label className={labelCls}>Last service / done date</label>
        <input type="date" {...register(`${fieldPrefix}.start_date`, { required: true })} className={inputCls} />
      </div>

      {/* Repeat interval (hidden for one-time) */}
      {!oneTime && (
        <div>
          <label className={labelCls}>Repeat every</label>
          <div className="flex gap-2">
            <Controller
              name={`${fieldPrefix}.repeat_every`}
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field }) => (
                <input type="number" min={1} max={100} {...field} className={`${inputCls} w-20`} />
              )}
            />
            <Controller
              name={`${fieldPrefix}.repeat_unit`}
              control={control}
              render={({ field }) => (
                <select {...field} className={`${inputCls} flex-1`}>
                  {repeatUnits.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              )}
            />
          </div>
        </div>
      )}

      {/* Alert + one-time row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Alert (days before due)</label>
          <Controller
            name={`${fieldPrefix}.remind_before_days`}
            control={control}
            rules={{ required: true, min: 0 }}
            render={({ field }) => (
              <input type="number" min={0} max={365} {...field} className={inputCls} />
            )}
          />
        </div>
        <div className="flex items-end pb-0.5">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" {...register(`${fieldPrefix}.one_time`)} className="w-4 h-4 text-brand-600 rounded" />
            One-time only
          </label>
        </div>
      </div>

      {/* Next due preview */}
      {nextDueDateStr && (
        <div className="rounded-lg bg-brand-50 border border-brand-100 px-3 py-2 text-xs space-y-0.5">
          <div className="flex justify-between">
            <span className="text-brand-600 font-medium">Next due</span>
            <span className="text-brand-800 font-semibold">{nextDueDateStr}</span>
          </div>
          {alertDateStr && (
            <div className="flex justify-between">
              <span className="text-brand-500">Alert fires on</span>
              <span className="text-brand-700">{alertDateStr}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
